import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { reuploadableItems } from "./clientDocuments.utils";
import { assigneeOptionsFor } from "./requestRouting";

/**
 * Picks which documents to ask again, why, and who to ask — the client directly or the
 * referral partner (mirrors the first-time request routing). A partner routing notifies the
 * portal (no client email); the partner then collects from the client or uploads themselves,
 * and the client email for a direct routing uses the reupload message, not the original.
 */
const FlagForReuploadDialog = ({ open, request, client, loading, onCancel, onConfirm }) => {
  const [selection, setSelection] = useState({});
  const [formError, setFormError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assignee, setAssignee] = useState("CLIENT");

  const items = reuploadableItems(request);
  const assigneeOptions = assigneeOptionsFor(client || {});
  const isPartnerRouting = assignee === "PARTNER";

  // Reopening for a different request must not carry the previous one's ticks over.
  // Default routing follows the parent: a partner-routed request goes back to the partner.
  useEffect(() => {
    if (open) {
      setSelection({});
      setFormError(null);
      setConfirmOpen(false);
      setAssignee(request?.assigneeType === "PARTNER" ? "PARTNER" : "CLIENT");
    }
  }, [open, request?.id, request?.assigneeType]);

  const toggle = (itemId) =>
    setSelection((current) => {
      const next = { ...current };
      if (next[itemId] === undefined) {
        next[itemId] = "";
      } else {
        delete next[itemId];
      }
      return next;
    });

  const setReason = (itemId, reason) =>
    setSelection((current) => ({ ...current, [itemId]: reason }));

  const chosen = Object.entries(selection).map(([itemId, reason]) => ({
    itemId: Number(itemId),
    reason: reason.trim() || null,
  }));

  const handleSubmit = () => {
    if (chosen.length === 0) {
      setFormError("Pick at least one document to request again.");
      return;
    }
    setFormError(null);
    setConfirmOpen(true);
  };

  const handleClose = () => {
    if (loading) return;
    onCancel();
  };

  const confirmMessage = isPartnerRouting
    ? `This notifies the referral partner of a new upload round for ${chosen.length} document${
        chosen.length === 1 ? "" : "s"
      } — no email goes to the client. The partner collects from the client or uploads themselves, like a new request. The files already on this request are kept until a replacement arrives.`
    : `This emails the client a new upload link for ${chosen.length} document${
        chosen.length === 1 ? "" : "s"
      } with the reupload message (not the original request message). The files already on this request are kept until a replacement arrives.`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request documents again</DialogTitle>
      <DialogContent className="space-y-3">
        <p className="text-sm text-app-muted">
          A new round listing only what you tick here. Files already sent stay on this request until
          a replacement arrives.
        </p>
        {formError && <Alert severity="error">{formError}</Alert>}
        {items.length === 0 && (
          <Alert severity="info">
            Every document on this request has already been asked for again.
          </Alert>
        )}
        {items.map((item) => {
          const checked = selection[item.id] !== undefined;
          return (
            <div key={item.id} className="border border-app-border rounded-lg p-3">
              <FormControlLabel
                control={<Checkbox checked={checked} onChange={() => toggle(item.id)} />}
                label={item.label}
              />
              {checked && (
                <TextField
                  fullWidth
                  size="small"
                  className="mt-2"
                  label="Reason (optional)"
                  placeholder="e.g. the scan is cut off at the bottom"
                  value={selection[item.id]}
                  onChange={(event) => setReason(item.id, event.target.value)}
                />
              )}
            </div>
          );
        })}
        {assigneeOptions.length > 1 && (
          <FormControl component="fieldset" className="pt-1">
            <FormLabel component="legend">Send to</FormLabel>
            <RadioGroup row value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              {assigneeOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio size="small" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {isPartnerRouting && (
              <p className="text-xs text-app-muted m-0">
                The referral partner is notified in the portal and collects or uploads like a new
                request. The client gets no email unless the partner forwards the link.
              </p>
            )}
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || items.length === 0}>
          {loading ? "Sending..." : "Request reupload"}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmOpen}
        title={
          isPartnerRouting
            ? "Ask the referral partner for these again?"
            : "Ask the client for these again?"
        }
        message={confirmMessage}
        confirmLabel="Send request"
        loading={loading}
        onConfirm={() =>
          onConfirm(chosen, { assignee, assigneePartnerId: client?.referralPartnerId })
        }
        onCancel={() => setConfirmOpen(false)}
      />
    </Dialog>
  );
};

export default FlagForReuploadDialog;
